import { useEffect } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';
import { change, getFormValues } from 'redux-form';

import AmenityFormComponent from './AmenityFormComponent';

import { getBuilding, getBuildingsForFilter } from '../Building/actionCreators';
import {
  clearAmenities,
  confirmAction,
  createAmenity,
  getAmenity,
  getDoors,
  updateAmenity,
} from './actions';

import { formatSelect } from '../Forms/utils';
import {
  getAmenityDefaultInitialValues,
  getAmenityInitialValues,
  getBuildingFilterOptions,
  getBuildingTimezone,
  getMomentDateAndTime,
  schedulePreSubmit,
} from './utils';

import usePrevious from '../../hooks/usePrevious';

const form = 'Amenity';

const AmenityFormContainer = (props) => {
  const {
    clearAmenities,
    onCreateAmenity,
    onUpdateAmenity,
    onConfirmUpdateAmenity,
    building,
    getBuilding,
    initialValues,
  } = props;

  const prevBuilding = usePrevious(building);

  useEffect(
    () => () => {
      clearAmenities();
    },
    [],
  );

  useEffect(() => {
    if (building && building !== prevBuilding) {
      getBuilding(building);
    }
  }, [building]);

  const onSubmit = (data) => {
    const {
      minimumReservationTime,
      maximumReservationTime,
      schedule: rawSchedule,
      ...rest
    } = data;
    const [, minTime] = getMomentDateAndTime(minimumReservationTime);
    const [, maxTime] = getMomentDateAndTime(maximumReservationTime);
    const schedule = schedulePreSubmit(rawSchedule);
    const values = {
      ...rest,
      minimumReservationTime: `${minTime.slice(0, 6)}00`,
      maximumReservationTime: `${maxTime.slice(0, 6)}00`,
      schedule,
    };
    if (
      values?.id &&
      !values?.active &&
      values?.upcomingReservationsCount > 0
    ) {
      const bodyText = (
        <span>
          There are <b>{values?.upcomingReservationsCount}</b> existing
          reservations on <b>{values?.name}</b>. If you inactivate{' '}
          <b>{values?.name}</b>, it will automatically cancel all existing
          reservations. Do you wish to proceed?
        </span>
      );
      return onConfirmUpdateAmenity(bodyText, values, updateAmenity);
    }
    if (values?.id) {
      const imageIdToRemoved = values?.images?.length
        ? initialValues?.images.reduce((itemsToDel, initItem) => {
            values?.images?.forEach((image) => {
              initItem?.id !== image?.id && itemsToDel.push(initItem.id);
            });
            return itemsToDel;
          }, [])
        : initialValues?.images
            ?.map((itemsToDel) => itemsToDel?.id)
            .filter(Boolean) ?? [];

      return onUpdateAmenity(values, imageIdToRemoved);
    }

    return onCreateAmenity(values);
  };

  return <AmenityFormComponent {...props} onSubmit={onSubmit} />;
};

const mapStateToProps = (state, ownProps) => {
  const { common, buildings, amenity } = state;
  const pmcId = ownProps?.match?.params?.PMCId;
  const buildingId = ownProps?.match?.params?.buildingId;
  const amenityId = ownProps?.match?.params?.id;
  const isCreate = amenityId === 'create';

  const formValues = getFormValues(form)(state);
  const amenityData = amenity?.details?.[amenityId]?.data ?? {};
  const isReservationAgreements = formValues?.rentalAgreementRequired;
  const doorsToAssign = amenity?.doorsToAssign;

  const initialValues = isCreate
    ? getAmenityDefaultInitialValues(buildingId)
    : getAmenityInitialValues(amenityData);

  const buildingOptions = getBuildingFilterOptions(
    buildings,
    pmcId,
    buildingId,
  );

  const amenityDoors = Array.isArray(doorsToAssign) ? doorsToAssign : [];

  const connectedDoorsOptions = formatSelect(amenityDoors);

  if (isCreate && !buildingId && buildingOptions?.length === 1) {
    initialValues.building = buildingOptions?.[0]?.value;
  }

  const building = formValues?.building;
  const selectedBuilding = buildings?.[building];
  const isRentalBuilding = selectedBuilding?.type === 'rental';
  const timezone = getBuildingTimezone(common, buildings, building);

  const startTime = formValues?.minimumReservationTime;
  const endTime = formValues?.maximumReservationTime;
  const connectedDoors = formValues?.connectedDoors;

  return {
    isCreate,
    form,
    building,
    amenityId,
    isRentalBuilding,
    startTime,
    endTime,
    timezone,
    initialValues,
    buildingOptions,
    isReservationAgreements,
    connectedDoorsOptions,
    connectedDoors,
  };
};

AmenityFormContainer.defaultProps = {
  building: null,
};

AmenityFormContainer.propTypes = {
  clearAmenities: PropTypes.func.isRequired,
  onCreateAmenity: PropTypes.func.isRequired,
  onUpdateAmenity: PropTypes.func.isRequired,
  onConfirmUpdateAmenity: PropTypes.func.isRequired,
  getBuilding: PropTypes.func.isRequired,
  building: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialValues: PropTypes.instanceOf(Object).isRequired,
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const amenityId = ownProps?.match?.params?.id;
  const isCreate = amenityId === 'create';
  return {
    onLoad: () => {
      getBuildingsForFilter(dispatch);
      !isCreate && getAmenity(dispatch, amenityId);
    },
    getBuilding: (buildingId) => {
      getBuilding(dispatch, buildingId);
      getDoors(dispatch, {}, buildingId);
    },
    clearAmenities: () => clearAmenities(dispatch),
    onFieldChange: (field, value) => dispatch(change(form, field, value)),
    onConfirmUpdateAmenity: (bodyText, data, callback) =>
      confirmAction(dispatch, bodyText, data, callback),
    onCreateAmenity: (data) => createAmenity(dispatch, data),
    onUpdateAmenity: (data, imageIdToRemoved) =>
      updateAmenity(dispatch, data, imageIdToRemoved),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AmenityFormContainer);
